import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import PropertyCard from '../components/PropertyCard'
import ResaleFilterBar, { PROPERTY_TYPES, PRICE_MIN_ABS, PRICE_MAX_ABS } from '../components/ResaleFilterBar'
import { useLanguage } from '../context/LanguageContext'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"
const API_URL = import.meta.env.VITE_API_URL || ''

// ── Default filters ────────────────────────────────────────────────────────
const DEFAULT_FILTERS = {
  searchQuery:   '',
  minPrice:      PRICE_MIN_ABS,
  maxPrice:      PRICE_MAX_ABS,
  bedrooms:      [],
  areas:         [],
  lifestyles:    [],
  propertyTypes: [],   // slug array — empty = all
  currency:      'AED',
  sortBy:        'default',
}

// MODE_COPY is now built inside component using t()

// ── Lifestyle inference (client-side) ──────────────────────────────────────
function inferLifestyle(prop) {
  const tags = []
  const nameLower  = (prop.name  || '').toLowerCase()
  const areaLower  = (prop.area  || '').toLowerCase()
  const typeLower  = (prop.type  || '').toLowerCase()
  const price      =  prop.rawAmount || 0

  // 🌊 Waterfront
  if (/marina|harbour|harbor|beach|palm|creek|waterfront|canal/.test(areaLower + nameLower)) tags.push('waterfront')

  // 👨‍👩‍👧 Family
  if (/jvc|jumeirah village|dubai hills|mbr|meydan|arabian ranches|town square|akoya/.test(areaLower) ||
      /villa|townhouse/.test(typeLower)) tags.push('family')

  // 💎 Luxury (high price or premium area)
  if (price > 3_000_000 || /palm|downtown|difc|bluewaters/.test(areaLower)) tags.push('luxury')

  // 📈 Investment (lower entry or yield-friendly area)
  if ((price > 0 && price < 2_000_000) ||
      /jvc|studio city|arjan|al barsha|discovery gardens/.test(areaLower)) tags.push('investment')

  return tags
}

// ── Sort ───────────────────────────────────────────────────────────────────
function sortProps(list, sortBy) {
  if (!sortBy || sortBy === 'default') return list
  const copy = [...list]
  if (sortBy === 'price_asc')  return copy.sort((a, b) => (a.rawAmount || 0) - (b.rawAmount || 0))
  if (sortBy === 'price_desc') return copy.sort((a, b) => (b.rawAmount || 0) - (a.rawAmount || 0))
  return copy
}

// ── Skeleton card ──────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div style={{
      borderRadius: '16px', overflow: 'hidden', background: '#fff',
      border: '1px solid rgba(0,46,83,0.08)', boxShadow: '0 4px 16px rgba(0,46,83,0.06)',
    }}>
      <div style={{ height: '220px', background: 'linear-gradient(90deg, #f0f4f8 25%, #e2e8f0 50%, #f0f4f8 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ height: '18px', width: '75%', background: '#e2e8f0', borderRadius: '6px' }} />
        <div style={{ height: '13px', width: '50%', background: '#e2e8f0', borderRadius: '6px' }} />
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ height: '24px', width: '70px', background: '#e2e8f0', borderRadius: '6px' }} />
          <div style={{ height: '24px', width: '55px', background: '#e2e8f0', borderRadius: '6px' }} />
        </div>
        <div style={{ height: '40px', background: '#e2e8f0', borderRadius: '10px', marginTop: '8px' }} />
      </div>
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function PropertiesPage() {
  const { mode = 'buy', propertyType } = useParams()
  const navigate  = useNavigate()
  const { t } = useLanguage()

  // Normalise mode — only 'buy' or 'rent' are valid
  const activeMode = mode === 'rent' ? 'rent' : 'buy'
  const apiCategory = activeMode === 'rent' ? 'rent' : 'sale'

  // Build hero copy from translations
  const copy = {
    badge:    t(`props.${activeMode}.badge`),
    headline: t(`props.${activeMode}.headline`),
    accent:   t(`props.${activeMode}.accent`),
    sub:      t(`props.${activeMode}.sub`),
  }

  const [filters, setFilters]               = useState(DEFAULT_FILTERS)
  const [properties, setProperties]         = useState([])
  const [total, setTotal]                   = useState(0)
  const [loading, setLoading]               = useState(true)
  const [error, setError]                   = useState(null)

  // Fallback "other" properties when primary fetch returns empty
  const [otherProperties, setOtherProperties] = useState([])
  const [otherLoading, setOtherLoading]       = useState(false)
  const debounceRef = useRef(null)

  // Always fetch ALL properties for the mode — type filtering is done client-side
  const fetchProperties = useCallback((category) => {
    setLoading(true)
    setError(null)
    setProperties([])
    setOtherProperties([])

    fetch(`${API_URL}/api/properties?category=${category}&perPage=100`)
      .then(r => { if (!r.ok) throw new Error(`Server error ${r.status}`); return r.json() })
      .then(data => {
        setProperties(data.properties || [])
        setTotal(data.total ?? 0)
        setLoading(false)
      })
      .catch(err => {
        console.error('[PropertiesPage] fetch error:', err)
        setError('Unable to load listings right now. Please try again shortly.')
        setLoading(false)
      })
  }, [])  // stable — no URL deps needed

  // Reset all filters on mode or url change — nothing pre-selected, user picks
  useEffect(() => {
    setFilters(DEFAULT_FILTERS)
  }, [activeMode, propertyType])

  // Refetch when mode changes (re-fetch all for new category)
  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchProperties(apiCategory), filters.searchQuery ? 400 : 0)
    return () => clearTimeout(debounceRef.current)
  }, [activeMode, fetchProperties, apiCategory])

  const fallbackCategory = activeMode === 'rent' ? 'sale' : 'rent'

  // ── Fallback: if empty, load properties of the OPPOSITE mode ──────────────
  useEffect(() => {
    if (!loading && !error && properties.length === 0) {
      setOtherLoading(true)
      fetch(`${API_URL}/api/properties?category=${fallbackCategory}&perPage=12`)
        .then(r => r.json())
        .then(data => { setOtherProperties(data.properties || []); setOtherLoading(false) })
        .catch(() => setOtherLoading(false))
    }
  }, [loading, error, properties.length, fallbackCategory])

  // ── Client-side: tag + filter ──────────────────────────────────────────
  const tagged = properties.map(p => ({
    ...p,
    lifestyleTags: inferLifestyle(p),
  }))

  const visible = sortProps(
    tagged.filter(p => {
      // Lifestyle — must match ALL selected
      if (filters.lifestyles.length > 0) {
        if (!filters.lifestyles.every(l => p.lifestyleTags.includes(l))) return false
      }
      // Property type — any selected match
      if (filters.propertyTypes.length > 0) {
        const typeLower = (p.type || '').toLowerCase()
        const matched = filters.propertyTypes.some(slug => {
          const typeMap = {
            residential: ['apartment', 'villa', 'townhouse', 'penthouse', 'studio', 'duplex'],
            office:      ['office space', 'retail', 'shop'],
            warehouse:   ['warehouse', 'industrial unit'],
            hotel:       ['hotel', 'hotel apartment'],
            plots:       ['land', 'plot'],
            buildings:   ['whole building', 'building'],
          }
          return (typeMap[slug] || []).some(t => typeLower.includes(t))
        })
        if (!matched) return false
      }
      // Area text match
      if (filters.areas.length > 0) {
        const aLower = (p.area || '').toLowerCase()
        if (!filters.areas.some(a => aLower.includes(a.toLowerCase().split(' (')[0]))) return false
      }
      // Search
      if (filters.searchQuery) {
        const q = filters.searchQuery.toLowerCase()
        if (
          !(p.name || '').toLowerCase().includes(q) &&
          !(p.area || '').toLowerCase().includes(q) &&
          !(p.type || '').toLowerCase().includes(q)
        ) return false
      }
      // Budget (display price is a formatted string, so compare rawAmount if available)
      if (filters.minPrice > PRICE_MIN_ABS || filters.maxPrice < PRICE_MAX_ABS) {
        const amount = p.rawAmount || 0
        if (amount > 0) {
          if (amount < filters.minPrice || amount > filters.maxPrice) return false
        }
      }
      return true
    }),
    filters.sortBy
  )

  const isEmpty = !loading && !error && properties.length === 0

  const switchMode = (newMode) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    navigate(`/properties/${newMode}`)
  }

  return (
    <div style={{ fontFamily: FONT }}>
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes fadeIn  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        .prop-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 24px; }
        @media(max-width:1024px){ .prop-grid { grid-template-columns: repeat(2,1fr) !important; } }
        @media(max-width:640px) { .prop-grid { grid-template-columns: 1fr !important; } }
      `}</style>

      <Navbar />

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <div style={{
        background: `linear-gradient(135deg, ${NAVY} 0%, #001a33 60%, #0a2d4a 100%)`,
        padding: '130px 20px 0',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative orbs */}
        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '380px', height: '380px', borderRadius: '50%', background: `${GOLD}12`, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '260px', height: '260px', borderRadius: '50%', background: `${GOLD}08`, pointerEvents: 'none' }} />

        {/* Hero content */}
        <div style={{ position: 'relative', zIndex: 1, paddingBottom: '48px' }}>
          <span style={{
            display: 'inline-block', background: `${GOLD}20`, color: GOLD,
            fontFamily: FONT, fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px',
            padding: '6px 18px', borderRadius: '50px', marginBottom: '18px',
            textTransform: 'uppercase', border: `1px solid ${GOLD}40`,
          }}>
            {copy.badge}
          </span>

          <h1 style={{
            fontFamily: FONT, fontSize: 'clamp(28px, 4vw, 52px)', fontWeight: 700,
            color: '#fff', margin: '0 0 14px', letterSpacing: '-0.5px', lineHeight: 1.2,
          }}>
            {copy.headline}<br />
            <span style={{ color: GOLD }}>{copy.accent}</span>
          </h1>

          <p style={{
            fontFamily: FONT, fontSize: '16px', color: 'rgba(255,255,255,0.72)',
            maxWidth: '520px', margin: '0 auto 20px', lineHeight: 1.7,
          }}>
            {copy.sub}
          </p>

              {total > 0 && (
            <div style={{
              display: 'inline-block', background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)', borderRadius: '50px',
              padding: '6px 20px', fontFamily: FONT, fontSize: '13px',
              fontWeight: 600, color: 'rgba(255,255,255,0.85)',
            }}>
              { t('props.listingCount')(total) }
            </div>
          )}
        </div>

        {/* ── Buy / Rent tab bar — anchored to bottom of hero ── */}
        <div style={{
          position: 'relative', zIndex: 2,
          display: 'flex', justifyContent: 'center',
          gap: '0',
        }}>
          {[
            { mode: 'buy',  label: t('props.tabBuy'),  icon: '🏡' },
            { mode: 'rent', label: t('props.tabRent'), icon: '🔑' },
          ].map(({ mode: m, label, icon }) => {
            const isOn = activeMode === m
            return (
              <button
                key={m}
                id={`mode-tab-${m}`}
                onClick={() => switchMode(m)}
                style={{
                  fontFamily: FONT, fontSize: '15px', fontWeight: 700,
                  padding: '16px 52px',
                  border: 'none', cursor: 'pointer',
                  transition: 'all 0.22s',
                  background: isOn ? '#fff' : 'rgba(255,255,255,0.10)',
                  color: isOn ? NAVY : 'rgba(255,255,255,0.6)',
                  borderRadius: isOn ? '12px 12px 0 0' : '0',
                  boxShadow: isOn ? '0 -4px 20px rgba(0,0,0,0.12)' : 'none',
                  borderBottom: isOn ? `3px solid ${GOLD}` : '3px solid transparent',
                  letterSpacing: '0.3px',
                }}
              >
                <span style={{ marginRight: '8px', fontSize: '16px' }}>{icon}</span>
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* ── Filter Bar ───────────────────────────────────────────────── */}
      <div style={{ background: '#fff' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '48px 20px 0', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <span style={{
                display: 'inline-block', background: `${GOLD}18`, color: GOLD,
                fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.8px',
                padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase',
                border: `1px solid ${GOLD}35`, marginBottom: '10px',
              }}>
              { t('props.portfolio') }
              </span>
              <h2 style={{ fontFamily: FONT, fontSize: 'clamp(22px,2.5vw,34px)', fontWeight: 700, color: NAVY, margin: 0 }}>
                {activeMode === 'buy' ? t('props.forSale') : t('props.forRent') }
              </h2>
            </div>
            <a href="/sell-rent" style={{
              display: 'inline-block', color: NAVY,
              fontFamily: FONT, fontSize: '14px', fontWeight: 600,
              textDecoration: 'none', borderBottom: `2px solid ${GOLD}`,
              paddingBottom: '2px', transition: 'color 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.color = GOLD}
              onMouseLeave={e => e.currentTarget.style.color = NAVY}>
              { t('props.listYourProperty') }
            </a>
          </div>
        </div>

        <ResaleFilterBar
          filters={filters}
          onFilterChange={setFilters}
          areaList={[...new Set(properties.map(p => p.area ? p.area.split(' (')[0].trim() : '').filter(Boolean))]}
          resultCount={visible.length}
          totalCount={properties.length}
          loading={loading}
        />

        {/* ── Property grid ── */}
        <div style={{ background: '#f4f7fb', padding: '32px 20px 80px' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

            {/* Error */}
            {error && (
              <div style={{
                textAlign: 'center', padding: '48px', background: '#fff8f8',
                borderRadius: '16px', border: '1px solid #fecaca',
                color: '#991b1b', fontFamily: FONT, fontSize: '15px', marginBottom: '32px',
              }}>
                ⚠️ {error}
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="prop-grid">
                {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            )}

            {/* Results */}
            {!loading && !error && visible.length > 0 && (
              <div className="prop-grid">
                {visible.map((p, i) => <PropertyCard key={p.id || i} project={p} />)}
              </div>
            )}

            {/* Filtered empty — some results exist but filters narrowed to 0 */}
            {!loading && !error && properties.length > 0 && visible.length === 0 && (
              <div style={{
                textAlign: 'center', padding: '64px 20px',
                background: '#fafbfc', borderRadius: '20px',
                border: '2px dashed rgba(0,46,83,0.1)',
              }}>
                <div style={{ fontSize: '52px', marginBottom: '16px' }}>🔍</div>
                <div style={{ fontFamily: FONT, fontSize: '20px', fontWeight: 700, color: NAVY, marginBottom: '10px' }}>
                  { t('props.noMatchTitle') }
                </div>
                <div style={{ fontFamily: FONT, fontSize: '14px', color: '#6b7a8d', marginBottom: '24px' }}>
                  { t('props.noMatchDesc') }
                </div>
                <button
                  onClick={() => setFilters(DEFAULT_FILTERS)}
                  style={{
                    background: GOLD, color: '#fff', border: 'none',
                    fontFamily: FONT, fontSize: '14px', fontWeight: 700,
                    padding: '13px 28px', borderRadius: '10px', cursor: 'pointer',
                  }}>
                  { t('props.clearFilters') }
                </button>
              </div>
            )}

            {/* ── Empty state: no listings for this type at all ── */}
            {isEmpty && (
              <>
                {/* Notice */}
                <div style={{
                  background: '#fff',
                  borderRadius: '20px',
                  border: `1px solid rgba(0,46,83,0.10)`,
                  borderLeft: `4px solid ${GOLD}`,
                  padding: '44px 48px',
                  marginBottom: '60px',
                  display: 'flex',
                  gap: '24px',
                  alignItems: 'flex-start',
                  animation: 'fadeIn 0.4s ease',
                }}>
                  <div style={{
                    width: '56px', height: '56px', borderRadius: '14px',
                    background: `${GOLD}15`, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: '26px', flexShrink: 0,
                  }}>
                    {activeMode === 'buy' ? '🏡' : '🔑'}
                  </div>
                  <div>
                    <div style={{ fontFamily: FONT, fontSize: '20px', fontWeight: 700, color: NAVY, marginBottom: '10px' }}>
                      {activeMode === 'buy' ? t('props.emptyBuy') : t('props.emptyRent') }
                    </div>
                    <p style={{ fontFamily: FONT, fontSize: '15px', color: '#5a6577', lineHeight: 1.7, margin: '0 0 20px' }}>
                      { t('props.emptyDesc')(activeMode) }
                    </p>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <a href="/contact" style={{
                        display: 'inline-block', background: NAVY, color: '#fff',
                        fontFamily: FONT, fontSize: '13px', fontWeight: 700,
                        padding: '11px 24px', borderRadius: '8px', textDecoration: 'none',
                      }}>
                        { t('props.speakAgent') }
                      </a>
                      <a href="/sell-rent" style={{
                        display: 'inline-block', background: `${NAVY}10`,
                        border: `1.5px solid ${NAVY}20`,
                        color: NAVY, fontFamily: FONT, fontSize: '13px', fontWeight: 600,
                        padding: '11px 24px', borderRadius: '8px', textDecoration: 'none',
                      }}>
                        { t('props.listProp') }
                      </a>
                    </div>
                  </div>
                </div>

                {/* Other available properties */}
                <div style={{ marginBottom: '32px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '10px' }}>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(0,46,83,0.10)' }} />
                    <span style={{
                      fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.6px',
                      color: GOLD, textTransform: 'uppercase',
                    }}>{ t('props.exploreOther') }</span>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(0,46,83,0.10)' }} />
                  </div>
                  <h3 style={{ fontFamily: FONT, fontSize: 'clamp(18px,2vw,26px)', fontWeight: 700, color: NAVY, margin: '0 0 6px' }}>
                    {activeMode === 'buy' ? t('props.otherForRent') : t('props.otherForSale') }
                  </h3>
                  <p style={{ fontFamily: FONT, fontSize: '14px', color: '#6b7a8d', margin: 0, lineHeight: 1.6 }}>
                    { t('props.otherDesc') }
                  </p>
                </div>

                {otherLoading ? (
                  <div className="prop-grid">
                    {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
                  </div>
                ) : otherProperties.length > 0 ? (
                  <div className="prop-grid">
                    {otherProperties.map((p, i) => <PropertyCard key={p.id || i} project={p} />)}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── CTA Banner ───────────────────────────────────────────────── */}
      <div style={{
        background: `linear-gradient(135deg, ${NAVY} 0%, #0a2d4a 100%)`,
        padding: '64px 20px',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <span style={{
            display: 'inline-block', background: `${GOLD}20`, color: GOLD,
            fontFamily: FONT, fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px',
            padding: '5px 14px', borderRadius: '50px', textTransform: 'uppercase',
            border: `1px solid ${GOLD}40`, marginBottom: '16px',
          }}>{ t('props.ctaBadge') }</span>
          <h2 style={{ fontFamily: FONT, fontSize: 'clamp(22px,3vw,36px)', fontWeight: 700, color: '#fff', margin: '0 0 14px' }}>
            { t('props.ctaHeading') }
          </h2>
          <p style={{ fontFamily: FONT, fontSize: '15px', color: 'rgba(255,255,255,0.72)', lineHeight: 1.7, marginBottom: '28px' }}>
            { t('props.ctaDesc') }
          </p>
          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/sell-rent" style={{
              display: 'inline-block', background: GOLD, color: '#fff',
              fontFamily: FONT, fontSize: '15px', fontWeight: 700,
              padding: '14px 32px', borderRadius: '10px', textDecoration: 'none',
            }}>
              { t('props.listYourProperty') }
            </a>
            <a href="/contact" style={{
              display: 'inline-block', background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.3)', color: '#fff',
              fontFamily: FONT, fontSize: '15px', fontWeight: 600,
              padding: '14px 32px', borderRadius: '10px', textDecoration: 'none',
            }}>
              { t('props.contactUs') }
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}
